// Stimulus controller for adding and removing nested association fields dynamically.
//
// Usage:
//
// 1. Template:
//    - Create a template element with data-storefront--nested-association-form-target="template".
//    - Inside it, generate a blank set of fields using simple_fields_for with a child_index of 'NEW_RECORD', e.g.:
//      <%= f.simple_fields_for :my_association, MyAssociationClass.new  do |assoc| %>
//        <%= assoc.simple_fields_for 'NEW_RECORD' do |record| %>
//          ...
//        <% end %>
//      <% end %>
//
// 2. Add Record Button:
//    - Place a button or link with data-storefront--nested-association-form-target="addRecordButton".
//    - Attach data-action="storefront--nested-association-form#addRecord" to it.
//    - New records will be inserted directly *above* this button.
//
// 3. Remove Record Button:
//    - Wrap each nested record inside a container with class "nested-record".
//    - Add data-new-record to the wrapper, set to "true" for new records and "false" for persisted records.
//    - Inside each wrapper, add a button or link with data-action="storefront--nested-association-form#removeRecord".
//
// Behavior:
//
// - When adding:
//    - Duplicates the template, replacing all instances of "NEW_RECORD" with a timestamp for unique field names.
//
// - When removing:
//    - New (unsaved) records are removed from the DOM immediately.
//    - Existing (persisted) records are marked for destruction by setting the hidden `_destroy` field to `1` and hiding the fields.
//
// Notes:
// - Ensure `f.input :_destroy, as: :hidden` (or similar) is present in your nested fields partial for persisted records.
//

import { Controller } from "stimulus";

export default class extends Controller {
  static targets = ["addRecordButton", "template"];

  addRecordButtonTarget: HTMLElement
  templateTarget: HTMLTemplateElement

  addRecord(event) {
    event.preventDefault();
    
    const timestamp = new Date().getTime();
    const content = this.template.innerHTML.replace(/NEW_RECORD/g, timestamp.toString());
    this.addRecordButton.insertAdjacentHTML('beforebegin', content);
  }

  removeRecord(event) {
    event.preventDefault();
    
    const wrapper = event.target.closest('.nested-record');
    
    if (wrapper.dataset.newRecord === "true") {
      wrapper.remove();
    } else {
      const destroyField = wrapper.querySelector("input[name*='[_destroy]']");
      if (destroyField) {
        destroyField.value = 1;
      }
      wrapper.style.display = "none";
    }
  }

  get template() {
    return this.templateTarget
  }

  get addRecordButton() {
    return this.addRecordButtonTarget
  }
}
